import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, FileText, Users, MessageSquare, Lock, Unlock,
  Eye, EyeOff, Key, Loader2, XCircle, UserPlus, AlertTriangle, Activity, 
  Save, Clock, Download, Inbox, Send, CheckCircle, Shield, Receipt, 
  StickyNote, CalendarCheck
} from 'lucide-react';
import { 
  CoachClientsTab, 
  CoachAgreementsTab, 
  CoachMessagesTab, 
  CoachActivityTab 
} from '@/components/admin/coach-profile';

const API_URL = window.location.origin;

const AdminCoachProfilePage = () => {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [coachData, setCoachData] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showAssignClientDialog, setShowAssignClientDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showLinkAccountDialog, setShowLinkAccountDialog] = useState(false);
  const [allMembers, setAllMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [activityData, setActivityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [messagesData, setMessagesData] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [linkAccountMode, setLinkAccountMode] = useState('create'); // 'create' or 'link'
  const [linkEmail, setLinkEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [linkingAccount, setLinkingAccount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCoachProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/coaches/${coachId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoachData(response.data);
    } catch (error) {
      console.error('Failed to fetch coach profile:', error);
      toast.error('Failed to load coach profile');
    } finally {
      setLoading(false);
    }
  }, [coachId]);

  const fetchCoachActivity = useCallback(async () => {
    setLoadingActivity(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/coaches/${coachId}/activity`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivityData(response.data);
    } catch (error) {
      console.error('Failed to fetch coach activity:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoadingActivity(false);
    }
  }, [coachId]);

  const fetchCoachMessages = useCallback(async () => {
    setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/coaches/${coachId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessagesData(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  }, [coachId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/messages`,
        { content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      toast.success('Message sent!');
      fetchCoachMessages(); // Refresh messages
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const fetchAllMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/admin/users`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllMembers(response.data.filter(u => u.role === 'member'));
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  };

  useEffect(() => {
    fetchCoachProfile();
  }, [fetchCoachProfile]);

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchCoachActivity();
    }
    if (activeTab === 'messages') {
      fetchCoachMessages();
    }
  }, [activeTab, fetchCoachActivity, fetchCoachMessages]);

  const handleTogglePublish = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/toggle-publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchCoachProfile();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  const handleToggleLock = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/toggle-lock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchCoachProfile();
    } catch (error) {
      toast.error('Failed to toggle lock status');
    }
  };

  const handleResetPassword = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/reset-password`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowResetPasswordDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmail = async (newEmail) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/update-email`,
        { email: newEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Email updated successfully');
      fetchCoachProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignClient = async () => {
    if (!selectedMemberId) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/clients`,
        { user_id: selectedMemberId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Client assigned successfully');
      setShowAssignClientDialog(false);
      setSelectedMemberId('');
      fetchCoachProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign client');
    } finally {
      setSaving(false);
    }
  };

  const handleLinkAccount = async () => {
    setLinkingAccount(true);
    try {
      const token = localStorage.getItem('token');
      // Prefer linkEmail input, then volition_email, then personal_email
      const emailToUse = linkEmail || coach?.volition_email || coach?.personal_email;
      const payload = linkAccountMode === 'create' 
        ? { action: 'create', email: emailToUse }
        : { action: 'link', user_id: selectedUserId };
      
      const response = await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/link-account`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowLinkAccountDialog(false);
      setLinkEmail('');
      setSelectedUserId('');
      fetchCoachProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link account');
    } finally {
      setLinkingAccount(false);
    }
  };

  const handleUnlinkAccount = async () => {
    if (!window.confirm('Are you sure you want to unlink this coach from their user account? They will lose access to the coach portal.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/admin/coaches/${coachId}/unlink-account`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Account unlinked successfully');
      fetchCoachProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink account');
    }
  };

  const handleUpdateClientStatus = async (userId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/admin/coaches/${coachId}/clients/${userId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Client marked as ${newStatus}`);
      fetchCoachProfile();
    } catch (error) {
      toast.error('Failed to update client status');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-forest" />
      </div>
    );
  }

  if (!coachData) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Coach not found</p>
        <Button onClick={() => navigate('/admin/coaches')} className="mt-4">
          Back to Coaches
        </Button>
      </div>
    );
  }

  const { coach, linked_user, clients, agreements, messages } = coachData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/coaches')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-forest">{coach.name}</h1>
              <p className="text-gray-500">{coach.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Publish Status */}
            <Badge 
              className={coach.is_published !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}
            >
              {coach.is_published !== false ? <Eye className="h-3 w-3 mr-1" /> : <EyeOff className="h-3 w-3 mr-1" />}
              {coach.is_published !== false ? 'Published' : 'Unpublished'}
            </Badge>
            {/* Lock Status */}
            <Badge 
              className={coach.is_locked ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}
            >
              {coach.is_locked ? <Lock className="h-3 w-3 mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
              {coach.is_locked ? 'Locked' : 'Unlocked'}
            </Badge>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand/50">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="h-4 w-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="clients" className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Clients ({clients.length})
            </TabsTrigger>
            <TabsTrigger value="agreements" className="flex items-center gap-2">
              <FileText className="h-4 w-4" /> Agreements ({agreements.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> Messages ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex items-center gap-2">
              <Activity className="h-4 w-4" /> Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Photo & Quick Actions */}
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <img 
                      src={coach.image_url || '/placeholder-coach.jpg'} 
                      alt={coach.name}
                      className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-gold/20"
                    />
                    <h3 className="mt-4 font-semibold text-lg">{coach.name}</h3>
                    <p className="text-gray-500 text-sm">{coach.title}</p>
                    
                    <div className="mt-6 space-y-3">
                      <Button 
                        onClick={handleTogglePublish}
                        variant="outline"
                        className="w-full"
                      >
                        {coach.is_published !== false ? (
                          <><EyeOff className="h-4 w-4 mr-2" /> Unpublish Profile</>
                        ) : (
                          <><Eye className="h-4 w-4 mr-2" /> Publish Profile</>
                        )}
                      </Button>
                      <Button 
                        onClick={handleToggleLock}
                        variant="outline"
                        className="w-full"
                      >
                        {coach.is_locked ? (
                          <><Unlock className="h-4 w-4 mr-2" /> Unlock for Editing</>
                        ) : (
                          <><Lock className="h-4 w-4 mr-2" /> Lock Profile</>
                        )}
                      </Button>
                      {linked_user ? (
                        <>
                          <Button 
                            onClick={() => setShowResetPasswordDialog(true)}
                            variant="outline"
                            className="w-full"
                          >
                            <Key className="h-4 w-4 mr-2" /> Reset Password
                          </Button>
                          <Button 
                            onClick={handleUnlinkAccount}
                            variant="outline"
                            className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4 mr-2" /> Unlink Account
                          </Button>
                        </>
                      ) : (
                        <Button 
                          onClick={() => {
                            setLinkEmail(coach?.volition_email || coach?.personal_email || '');
                            fetchAllMembers();
                            setShowLinkAccountDialog(true);
                          }}
                          className="w-full bg-forest hover:bg-forest/90 text-white"
                        >
                          <UserPlus className="h-4 w-4 mr-2" /> Link User Account
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Details */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email</Label>
                      <div className="flex gap-2">
                        <Input 
                          defaultValue={coach?.volition_email || coach?.personal_email || ''} 
                          id="coach-email"
                          placeholder="coach@example.com"
                        />
                        <Button 
                          size="sm"
                          onClick={() => {
                            const email = document.getElementById('coach-email').value;
                            handleUpdateEmail(email);
                          }}
                          disabled={saving}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <Label>Calendly Link</Label>
                      <Input value={coach.calendly_link || ''} readOnly className="bg-gray-50" />
                    </div>
                  </div>

                  <div>
                    <Label>Bio</Label>
                    <Textarea value={coach.bio || ''} readOnly className="bg-gray-50" rows={4} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Specialties</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(coach.specialties || []).map((s, i) => (
                          <Badge key={i} variant="secondary">{s}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label>Credentials</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(coach.credentials || []).map((c, i) => (
                          <Badge key={i} variant="outline">{c}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-4 border-t">
                    <div className="flex items-center gap-2">
                      <Switch checked={coach.inner_edge_eligible || false} disabled />
                      <Label>Inner Edge Eligible</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={coach.is_black_female || false} disabled />
                      <Label>Black Female Coach</Label>
                    </div>
                  </div>

                  {linked_user && (
                    <div className="pt-4 border-t">
                      <Label className="text-forest">Linked User Account</Label>
                      <div className="mt-2 p-3 bg-green-50 rounded-lg">
                        <p className="text-sm"><strong>Name:</strong> {linked_user.full_name}</p>
                        <p className="text-sm"><strong>Email:</strong> {linked_user.email}</p>
                        <p className="text-sm"><strong>User ID:</strong> {linked_user.id}</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Clients Tab */}
          <TabsContent value="clients">
            <CoachClientsTab
              clients={clients}
              loading={loading}
              onUpdateStatus={handleUpdateClientStatus}
              onAssignClient={() => {
                fetchAllMembers();
                setShowAssignClientDialog(true);
              }}
            />
          </TabsContent>

          {/* Agreements Tab */}
          <TabsContent value="agreements">
            <CoachAgreementsTab agreements={agreements} loading={loading} onRefresh={fetchCoachProfile} />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-forest" />
                  Messages with {coach.name}
                </CardTitle>
                <CardDescription>
                  {coach.linked_user_id 
                    ? "Direct communication with this coach" 
                    : "Coach does not have a linked user account - messaging unavailable"}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!coach.linked_user_id ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <p className="text-gray-500">This coach doesn't have a linked user account yet.</p>
                    <p className="text-sm text-gray-400 mt-2">They need to create an account to receive messages.</p>
                  </div>
                ) : loadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-forest" />
                  </div>
                ) : (
                  <div className="flex flex-col h-[450px]">
                    {/* Messages List */}
                    <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-4 bg-sand/20 rounded-lg">
                      {(!messagesData?.messages || messagesData.messages.length === 0) ? (
                        <div className="text-center py-8">
                          <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p className="text-gray-500">No messages yet</p>
                          <p className="text-sm text-gray-400">Start the conversation below</p>
                        </div>
                      ) : (
                        messagesData.messages.map(message => (
                          <div 
                            key={message.id}
                            className={`flex ${message.is_admin ? 'justify-end' : 'justify-start'}`}
                          >
                            <div 
                              className={`max-w-[75%] p-3 rounded-lg ${
                                message.is_admin 
                                  ? 'bg-forest text-white rounded-br-none' 
                                  : 'bg-white border border-sand rounded-bl-none'
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`text-xs font-medium ${message.is_admin ? 'text-forest-100' : 'text-forest'}`}>
                                  {message.sender_name}
                                </span>
                                {message.is_admin && (
                                  <Badge className="bg-white/20 text-white text-[10px] px-1 py-0">Admin</Badge>
                                )}
                              </div>
                              <p className={`text-sm ${message.is_admin ? 'text-white' : 'text-gray-700'}`}>
                                {message.content}
                              </p>
                              <p className={`text-xs mt-2 ${message.is_admin ? 'text-forest-100' : 'text-gray-400'}`}>
                                {new Date(message.created_at).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="min-h-[60px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-forest hover:bg-forest/90 text-white self-end"
                      >
                        {sendingMessage ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Press Enter to send, Shift+Enter for new line</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-forest" />
                    Activity Log
                  </CardTitle>
                  <CardDescription>All actions and events related to this coach</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-500">Filter:</Label>
                  <Select value={activityFilter} onValueChange={setActivityFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Activity</SelectItem>
                      <SelectItem value="admin">Admin Actions</SelectItem>
                      <SelectItem value="clients">Client Activity</SelectItem>
                      <SelectItem value="sessions">Sessions</SelectItem>
                      <SelectItem value="communication">Messages</SelectItem>
                      <SelectItem value="finance">Invoices</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {loadingActivity ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-forest" />
                  </div>
                ) : !activityData ? (
                  <p className="text-gray-500 text-center py-8">No activity data available</p>
                ) : (
                  <>
                    {/* Stats Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-green-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-700">{activityData.stats?.active_clients || 0}</p>
                        <p className="text-sm text-green-600">Active Clients</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-blue-700">{activityData.stats?.completed_clients || 0}</p>
                        <p className="text-sm text-blue-600">Completed</p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg text-center">
                        <p className="text-2xl font-bold text-purple-700">{activityData.stats?.total_sessions || 0}</p>
                        <p className="text-sm text-purple-600">Total Sessions</p>
                      </div>
                    </div>

                    {/* Activity Timeline */}
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {activityData.activities
                        .filter(a => activityFilter === 'all' || a.category === activityFilter)
                        .map((activity, index) => {
                          const getIcon = () => {
                            switch (activity.type) {
                              case 'admin_action': return <Shield className="h-4 w-4 text-purple-600" />;
                              case 'client_assigned': return <UserPlus className="h-4 w-4 text-green-600" />;
                              case 'client_complete': return <CheckCircle className="h-4 w-4 text-blue-600" />;
                              case 'session_booked': return <CalendarCheck className="h-4 w-4 text-amber-600" />;
                              case 'invoice_submitted': return <Receipt className="h-4 w-4 text-emerald-600" />;
                              case 'agreement': return <FileText className="h-4 w-4 text-indigo-600" />;
                              case 'message_sent': return <Send className="h-4 w-4 text-blue-500" />;
                              case 'message_received': return <Inbox className="h-4 w-4 text-teal-500" />;
                              case 'note_created': return <StickyNote className="h-4 w-4 text-yellow-600" />;
                              default: return <Activity className="h-4 w-4 text-gray-500" />;
                            }
                          };

                          const getCategoryBadge = () => {
                            const colors = {
                              admin: 'bg-purple-100 text-purple-700',
                              clients: 'bg-green-100 text-green-700',
                              sessions: 'bg-amber-100 text-amber-700',
                              finance: 'bg-emerald-100 text-emerald-700',
                              agreements: 'bg-indigo-100 text-indigo-700',
                              communication: 'bg-blue-100 text-blue-700',
                              notes: 'bg-yellow-100 text-yellow-700'
                            };
                            return colors[activity.category] || 'bg-gray-100 text-gray-700';
                          };

                          return (
                            <div 
                              key={index}
                              className="flex items-start gap-3 p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              <div className="p-2 rounded-full bg-gray-100">
                                {getIcon()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="font-medium text-gray-900">{activity.description}</p>
                                  <Badge className={`text-xs ${getCategoryBadge()}`}>
                                    {activity.category}
                                  </Badge>
                                </div>
                                {activity.details && (
                                  <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                                )}
                                {activity.preview && (
                                  <p className="text-sm text-gray-500 italic mt-1 truncate">"{activity.preview}"</p>
                                )}
                                <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Unknown'}
                                  </span>
                                  {activity.by && (
                                    <span className="flex items-center gap-1">
                                      <User className="h-3 w-3" />
                                      {activity.by}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {activityData.activities.filter(a => activityFilter === 'all' || a.category === activityFilter).length === 0 && (
                        <p className="text-gray-500 text-center py-8">No activity found for this filter</p>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Assign Client Dialog */}
      <Dialog open={showAssignClientDialog} onOpenChange={setShowAssignClientDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Client to {coach.name}</DialogTitle>
            <DialogDescription>Select a member to assign as a client</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedMemberId} onValueChange={setSelectedMemberId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a member..." />
              </SelectTrigger>
              <SelectContent>
                {allMembers.map(member => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.full_name} ({member.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignClientDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignClient}
              disabled={!selectedMemberId || saving}
              className="bg-forest hover:bg-forest/90"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Assign Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reset Coach Password
            </DialogTitle>
            <DialogDescription>
              This will generate a new password and send it to the coach's email address.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600">
              A new temporary password will be sent to: <strong>{coach?.volition_email || coach?.personal_email || linked_user?.email}</strong>
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResetPasswordDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Account Dialog */}
      <Dialog open={showLinkAccountDialog} onOpenChange={(open) => {
        setShowLinkAccountDialog(open);
        if (!open) {
          setLinkAccountMode('create');
          setLinkEmail('');
          setSelectedUserId('');
          setSearchQuery('');
        }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-forest" />
              Link User Account
            </DialogTitle>
            <DialogDescription>
              Connect this coach profile to a user account so they can access the coach portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Mode Selection */}
            <div className="flex gap-2">
              <Button
                variant={linkAccountMode === 'create' ? 'default' : 'outline'}
                className={linkAccountMode === 'create' ? 'bg-forest' : ''}
                onClick={() => setLinkAccountMode('create')}
              >
                Create New Account
              </Button>
              <Button
                variant={linkAccountMode === 'link' ? 'default' : 'outline'}
                className={linkAccountMode === 'link' ? 'bg-forest' : ''}
                onClick={() => setLinkAccountMode('link')}
              >
                Link Existing User
              </Button>
            </div>

            {linkAccountMode === 'create' ? (
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    A new user account will be created and login credentials will be sent to the coach's email.
                  </p>
                </div>
                <div>
                  <Label htmlFor="link-email">Email Address</Label>
                  <Input
                    id="link-email"
                    type="email"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                    placeholder="coach@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Login credentials will be sent to this email
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    Link this coach profile to an existing user account. The user will gain coach portal access.
                  </p>
                </div>
                <div>
                  <Label>Search Users</Label>
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="mb-2"
                  />
                  <div className="max-h-48 overflow-y-auto border rounded-lg">
                    {allMembers
                      .filter(m => 
                        searchQuery === '' ||
                        m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        m.email?.toLowerCase().includes(searchQuery.toLowerCase())
                      )
                      .slice(0, 20)
                      .map(member => (
                        <div
                          key={member.id}
                          onClick={() => setSelectedUserId(member.id)}
                          className={`p-3 cursor-pointer hover:bg-sand/50 border-b last:border-b-0 ${
                            selectedUserId === member.id ? 'bg-forest/10 border-l-4 border-l-forest' : ''
                          }`}
                        >
                          <p className="font-medium text-sm">{member.full_name}</p>
                          <p className="text-xs text-gray-500">{member.email}</p>
                        </div>
                      ))}
                    {allMembers.filter(m => 
                      searchQuery === '' ||
                      m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                      m.email?.toLowerCase().includes(searchQuery.toLowerCase())
                    ).length === 0 && (
                      <p className="p-3 text-sm text-gray-500 text-center">No users found</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkAccountDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleLinkAccount}
              disabled={linkingAccount || (linkAccountMode === 'create' && !linkEmail) || (linkAccountMode === 'link' && !selectedUserId)}
              className="bg-forest hover:bg-forest/90 text-white"
            >
              {linkingAccount ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Processing...</>
              ) : linkAccountMode === 'create' ? (
                <><UserPlus className="h-4 w-4 mr-2" />Create & Link</>
              ) : (
                <><UserPlus className="h-4 w-4 mr-2" />Link Account</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCoachProfilePage;
